// Parsers de cuentas 2FA (otpauth://, exportación de Google Authenticator,
// ficheros de Aegis / 2FAS / andOTP en texto plano). Módulo puro, sin React ni
// DOM, para poder probarlo con Node. hooks/useOtpEntries.js lo reexporta.
//
// Una "entrada" es { type, issuer, account, secret, algorithm, digits, period, counter }
// sin id ni fecha (los pone useOtpEntries.addEntries).

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export const base32Decode = (input) => {
  let bits = '';
  const output = [];
  const clean = String(input || '').toUpperCase().replace(/[^A-Z2-7]/g, '');
  for (let i = 0; i < clean.length; i++) bits += B32.indexOf(clean[i]).toString(2).padStart(5, '0');
  for (let i = 0; i + 8 <= bits.length; i += 8) output.push(parseInt(bits.slice(i, i + 8), 2));
  return new Uint8Array(output);
};

export const bytesToBase32 = (bytes) => {
  let bits = '';
  let result = '';
  for (let i = 0; i < bytes.length; i++) bits += bytes[i].toString(2).padStart(8, '0');
  for (let i = 0; i + 5 <= bits.length; i += 5) result += B32[parseInt(bits.slice(i, i + 5), 2)];
  // Resto de bits (<5): se rellena con ceros, como hace el RFC 4648.
  const rest = bits.length % 5;
  if (rest) result += B32[parseInt(bits.slice(bits.length - rest).padEnd(5, '0'), 2)];
  return result;
};

/** Secreto en forma canónica: mayúsculas, sin espacios, guiones ni relleno '='. */
export const normalizeOtpSecret = (s) => String(s || '').replace(/[\s=-]/g, '').toUpperCase();

export const isValidOtpSecret = (s) => /^[A-Z2-7]{8,}$/.test(normalizeOtpSecret(s));

/** Clave de deduplicación: el secreto (dos cuentas con el mismo secreto generan los mismos códigos). */
export const otpDedupeKey = (entry) => normalizeOtpSecret(entry?.secret);

const ALGOS = { SHA1: 'SHA1', 'SHA-1': 'SHA1', SHA256: 'SHA256', 'SHA-256': 'SHA256', SHA512: 'SHA512', 'SHA-512': 'SHA512' };
const normAlgo = (a) => ALGOS[String(a || 'SHA1').toUpperCase()] || 'SHA1';
const normDigits = (d) => { const n = parseInt(d, 10); return n >= 6 && n <= 10 ? n : 6; };
const normPeriod = (p) => { const n = parseInt(p, 10); return n > 0 ? n : 30; };

export function normalizeOtpEntry({ type = 'totp', issuer = '', account = '', secret, algorithm, digits, period, counter }) {
  const s = normalizeOtpSecret(secret);
  if (!isValidOtpSecret(s)) return null;
  const entry = {
    type: String(type).toLowerCase() === 'hotp' ? 'hotp' : 'totp',
    issuer: String(issuer || '').trim(),
    account: String(account || '').trim(),
    secret: s,
    algorithm: normAlgo(algorithm),
    digits: normDigits(digits),
    period: normPeriod(period),
  };
  if (entry.type === 'hotp') entry.counter = parseInt(counter, 10) || 0;
  return entry;
}

const safeDecode = (s) => { try { return decodeURIComponent(s); } catch { return s; } };

/** otpauth://totp/Issuer:cuenta?secret=...&issuer=... → entrada, o null si no es válido. */
export const parseOtpAuthUrl = (url) => {
  try {
    const match = String(url || '').trim().match(/^otpauth:\/\/(totp|hotp)\/([^?]*)\?(.+)$/i);
    if (!match) return null;
    const label = safeDecode(match[2]);
    const params = new URLSearchParams(match[3]);
    const colon = label.indexOf(':');
    const labelIssuer = colon >= 0 ? label.slice(0, colon) : '';
    const account = colon >= 0 ? label.slice(colon + 1) : label;
    return normalizeOtpEntry({
      type: match[1],
      issuer: params.get('issuer') || labelIssuer,
      account,
      secret: params.get('secret'),
      algorithm: params.get('algorithm'),
      digits: params.get('digits'),
      period: params.get('period'),
      counter: params.get('counter'),
    });
  } catch {
    return null;
  }
};

// --- Google Authenticator: otpauth-migration://offline?data=<base64 protobuf> ---

function readVarint(bytes, start) {
  let result = 0;
  let shift = 0;
  let pos = start;
  while (pos < bytes.length) {
    const b = bytes[pos++];
    result += (b & 0x7f) * 2 ** shift;
    shift += 7;
    if (!(b & 0x80)) return [result, pos];
  }
  throw new Error('varint truncado');
}

// Recorre los campos de un mensaje protobuf. Solo se necesitan los wire types 0 y 2.
function* protoFields(bytes) {
  let pos = 0;
  while (pos < bytes.length) {
    let tag;
    [tag, pos] = readVarint(bytes, pos);
    const num = Math.floor(tag / 8);
    const wire = tag & 7;
    if (wire === 0) {
      let value;
      [value, pos] = readVarint(bytes, pos);
      yield { num, value };
    } else if (wire === 2) {
      let len;
      [len, pos] = readVarint(bytes, pos);
      yield { num, data: bytes.subarray(pos, pos + len) };
      pos += len;
    } else if (wire === 1) pos += 8;
    else if (wire === 5) pos += 4;
    else throw new Error(`wire type ${wire} no soportado`);
  }
}

/** otpauth-migration:// → lista de entradas; null si el texto no es de ese tipo. */
export const parseGoogleAuthMigration = (url) => {
  try {
    const text = String(url || '').trim();
    if (!text.startsWith('otpauth-migration://')) return null;
    const m = text.match(/[?&]data=([^&]+)/);
    if (!m) return null;
    let b64 = safeDecode(m[1]).replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

    const entries = [];
    for (const f of protoFields(bytes)) {
      if (f.num !== 1 || !f.data) continue; // 1 = OtpParameters; el resto es batch_size/index/id
      const raw = { type: 'totp', algorithm: 'SHA1', digits: 6 };
      let name = '';
      let issuer = '';
      for (const p of protoFields(f.data)) {
        if (p.num === 1 && p.data) raw.secret = bytesToBase32(p.data);
        else if (p.num === 2 && p.data) name = new TextDecoder().decode(p.data);
        else if (p.num === 3 && p.data) issuer = new TextDecoder().decode(p.data);
        else if (p.num === 4) raw.algorithm = { 2: 'SHA256', 3: 'SHA512' }[p.value] || 'SHA1';
        else if (p.num === 5) raw.digits = p.value === 2 ? 8 : 6;
        else if (p.num === 6) raw.type = p.value === 1 ? 'hotp' : 'totp';
        else if (p.num === 7) raw.counter = p.value;
      }
      const colon = name.indexOf(':');
      if (colon >= 0) {
        if (!issuer) issuer = name.slice(0, colon);
        name = name.slice(colon + 1);
      }
      const entry = normalizeOtpEntry({ ...raw, issuer, account: name });
      if (entry) entries.push(entry);
    }
    return entries;
  } catch {
    return null;
  }
};

/** Texto de un QR (otpauth:// u otpauth-migration://) → una o varias entradas. */
export function parseAnyOtpQr(qrText) {
  if (!qrText) return [];
  const migration = parseGoogleAuthMigration(qrText);
  if (migration && migration.length > 0) return migration;
  const single = parseOtpAuthUrl(qrText);
  return single ? [single] : [];
}

// --- Ficheros de otras apps de autenticación (sin cifrar) ---

/** Lista de URIs, una por línea (Aegis/2FAS/andOTP en texto plano). */
export function parseOtpUriText(text) {
  const entries = [];
  let invalid = 0;
  for (const rawLine of String(text || '').split(/\r?\n/)) {
    const line = rawLine.trim().replace(/^["']|["'],?;?$/g, '');
    if (!line || line.startsWith('#')) continue;
    const list = parseAnyOtpQr(line);
    if (list.length) entries.push(...list);
    else invalid++;
  }
  return { entries, skipped: invalid };
}

// Aegis: { header: { slots, params }, db: { entries: [{ type, name, issuer, info: {...} }] } }
export function parseAegisJson(obj) {
  if (obj?.header?.slots || typeof obj?.db === 'string') return { entries: [], skipped: 0, error: 'encrypted' };
  const entries = [];
  let skipped = 0;
  for (const e of obj?.db?.entries || []) {
    const type = String(e.type || '').toLowerCase();
    const entry = (type === 'totp' || type === 'hotp') && normalizeOtpEntry({
      type, issuer: e.issuer, account: e.name, secret: e.info?.secret,
      algorithm: e.info?.algo, digits: e.info?.digits, period: e.info?.period, counter: e.info?.counter,
    });
    if (entry) entries.push(entry); else skipped++; // steam, yandex, motp o secreto inválido
  }
  return { entries, skipped, error: null };
}

// 2FAS: { services: [{ name, secret, otp: { account, issuer, digits, period, algorithm, tokenType } }], servicesEncrypted }
export function parse2fasJson(obj) {
  if (typeof obj?.servicesEncrypted === 'string' && obj.servicesEncrypted) return { entries: [], skipped: 0, error: 'encrypted' };
  const entries = [];
  let skipped = 0;
  for (const s of obj?.services || []) {
    const kind = String(s.otp?.tokenType || 'TOTP').toUpperCase();
    const entry = (kind === 'TOTP' || kind === 'HOTP') && normalizeOtpEntry({
      type: kind, issuer: s.otp?.issuer || s.name, account: s.otp?.account,
      secret: s.secret, algorithm: s.otp?.algorithm, digits: s.otp?.digits,
      period: s.otp?.period, counter: s.otp?.counter,
    });
    if (entry) entries.push(entry); else skipped++;
  }
  return { entries, skipped, error: null };
}

// andOTP en claro: [{ secret, issuer, label, digits, type, algorithm, period, counter }]
export function parseAndOtpJson(list) {
  const entries = [];
  let skipped = 0;
  for (const e of list) {
    const kind = String(e.type || 'TOTP').toUpperCase();
    const entry = (kind === 'TOTP' || kind === 'HOTP') && normalizeOtpEntry({
      type: kind, issuer: e.issuer, account: e.label, secret: e.secret,
      algorithm: e.algorithm, digits: e.digits, period: e.period, counter: e.counter,
    });
    if (entry) entries.push(entry); else skipped++;
  }
  return { entries, skipped, error: null };
}

/**
 * Texto de un fichero → { entries, skipped, error, format }.
 * error: 'encrypted' (exportado con contraseña) | 'unknown' | null.
 */
export function parseOtpFileText(text) {
  const t = String(text || '').replace(/^﻿/, '').trim();
  if (!t) return { entries: [], skipped: 0, error: null, format: 'empty' };
  if (t.startsWith('{') || t.startsWith('[')) {
    let json;
    try { json = JSON.parse(t); } catch { return { entries: [], skipped: 0, error: 'unknown', format: 'json' }; }
    if (json?.db || json?.header) return { ...parseAegisJson(json), format: 'aegis' };
    if (json?.services || json?.servicesEncrypted !== undefined) return { ...parse2fasJson(json), format: '2fas' };
    if (Array.isArray(json) && json.every((e) => e && typeof e === 'object' && 'secret' in e)) {
      return { ...parseAndOtpJson(json), format: 'andotp' };
    }
    return { entries: [], skipped: 0, error: 'unknown', format: 'json' };
  }
  return { ...parseOtpUriText(t), error: null, format: 'uris' };
}

/**
 * Celda de "totp" de un gestor de contraseñas (Bitwarden, 1Password, LastPass):
 * una URI otpauth:// o un secreto base32 suelto. issuer/account de respaldo
 * vienen del ítem. steam:// y similares se ignoran (null).
 */
export function otpFromImportString(raw, { issuer = '', account = '' } = {}) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (/^otpauth:\/\//i.test(s)) {
    const e = parseOtpAuthUrl(s);
    if (!e) return null;
    if (!e.issuer) e.issuer = issuer;
    if (!e.account) e.account = account;
    return e;
  }
  return normalizeOtpEntry({ issuer, account, secret: s });
}

/** Separa `list` en nuevas y duplicadas (frente a `existing` y entre sí), por secreto. */
export function splitOtpDuplicates(list, existing = []) {
  const seen = new Set(existing.map(otpDedupeKey).filter(Boolean));
  const fresh = [];
  const duplicates = [];
  for (const e of list) {
    const key = otpDedupeKey(e);
    if (!key || seen.has(key)) { duplicates.push(e); continue; }
    seen.add(key);
    fresh.push(e);
  }
  return { fresh, duplicates };
}
